import React from 'react'
import { Avatar, Box, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import { Add, Delete, Remove } from '@mui/icons-material'
import { WARZONE_COIN_IMAGE_URL } from '../../../properties'
import { usePalworldCart } from '../../../contexts/PalworldCartContext'
import type { PalworldCartLine } from '../../../types/palworldCart'

const formatCoins = (value: number) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const Coin: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <Box
    component="img"
    src={WARZONE_COIN_IMAGE_URL}
    alt="Warzone Coin"
    sx={{ width: size, height: size, opacity: 0.95 }}
    loading="lazy"
  />
)

const PalworldCartItem: React.FC<{ line: PalworldCartLine }> = ({ line }) => {
  const { setQuantity, removeItem } = usePalworldCart()

  const unitPrice = Number(line.item.price ?? 0) || 0
  const subtotal = unitPrice * line.quantity

  const handleQty = (next: number) => {
    if (next <= 0) removeItem(line.id)
    else setQuantity(line.id, next)
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.05)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
        }}
      >
        <Avatar
          variant="rounded"
          src={line.item.imageUrl}
          alt={line.item.name ?? line.item.assetName ?? 'item'}
          sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(0,230,118,0.35)' }}
          imgProps={{ loading: 'lazy' }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }} noWrap>
            {line.item.name ?? '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            {line.item.category ?? '—'}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {formatCoins(unitPrice)}
            </Typography>
            <Coin />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              cada
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
            <IconButton size="small" onClick={() => handleQty(line.quantity - 1)} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}>
              <Remove fontSize="small" />
            </IconButton>
            <Chip label={line.quantity} size="small" sx={{ bgcolor: '#00e676', color: '#06110b', fontWeight: 900, minWidth: 44 }} />
            <IconButton size="small" onClick={() => handleQty(line.quantity + 1)} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}>
              <Add fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ textAlign: 'right', minWidth: 'fit-content' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              {formatCoins(subtotal)}
            </Typography>
            <Coin />
          </Stack>
          <IconButton size="small" onClick={() => removeItem(line.id)} sx={{ color: 'error.main', mt: 0.5 }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ opacity: 0.1, my: 1 }} />
    </Box>
  )
}

export default PalworldCartItem

